import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import StudentLayout from '../../components/StudentLayout';
import DocumentManager from '../../components/DocumentManager';
import { 
  ArrowLeft, Calendar, DollarSign, Clock, 
  CheckCircle, AlertCircle, Flag, FileText,
  PlayCircle, Upload, MessageCircle, Send, User
} from 'lucide-react';
import { format, formatDistance } from 'date-fns';

const StudentProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedTask, setExpandedTask] = useState(null);
  const [commentText, setCommentText] = useState({});

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`/api/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to fetch project details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'in-progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'on-hold': 'bg-gray-100 text-gray-800 border-gray-200',
      'review': 'bg-purple-100 text-purple-800 border-purple-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'text-red-500',
      'high': 'text-orange-500',
      'medium': 'text-yellow-500',
      'low': 'text-green-500'
    };
    return colors[priority] || 'text-gray-500';
  };

  const getTaskIcon = (status) => {
    const icons = {
      'pending': <Clock className="w-4 h-4" />,
      'in-progress': <PlayCircle className="w-4 h-4" />,
      'completed': <CheckCircle className="w-4 h-4" />
    };
    return icons[status] || <Clock className="w-4 h-4" />;
  };

  const calculateProgress = () => {
    if (!project?.tasks || project.tasks.length === 0) return 0;
    const completedTasks = project.tasks.filter(task => task.status === 'completed').length;
    return Math.round((completedTasks / project.tasks.length) * 100);
  };

  const getTotalPaid = () => {
    if (!project?.payments) return 0;
    return project.payments
      .filter(payment => payment.status === 'verified')
      .reduce((sum, payment) => sum + payment.amount, 0);
  };

  const uploadPaymentSlip = async (paymentId, file) => {
    const formData = new FormData();
    formData.append('slip', file);

    try {
      await axios.post(`/api/upload/payment-slip/${project._id}/${paymentId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Payment slip uploaded successfully!');
      fetchProject();
    } catch (error) {
      toast.error('Failed to upload payment slip');
    }
  };

  const addComment = async (taskId) => {
    const text = commentText[taskId];
    if (!text || text.trim() === '') {
      toast.error('Please enter a comment');
      return;
    }

    try {
      await axios.post(`/api/projects/${project._id}/tasks/${taskId}/comments`, {
        text: text.trim()
      });
      toast.success('Comment added successfully!');
      setCommentText({ ...commentText, [taskId]: '' });
      fetchProject();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const toggleTaskExpansion = (taskId) => {
    setExpandedTask(expandedTask === taskId ? null : taskId);
  };

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </StudentLayout>
    );
  }

  if (!project) {
    return (
      <StudentLayout>
        <div className="text-center py-8">
          <p className="text-gray-500">Project not found</p>
          <Link to="/student/projects" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
            ← Back to Projects
          </Link>
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Link 
            to="/student/projects" 
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Projects
          </Link>
          
          <span className={`px-3 py-2 rounded-lg border text-sm font-medium ${getStatusColor(project.status)}`}>
            {project.status}
          </span>
        </div>

        {/* Project Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Project Info */}
            <div className="lg:col-span-2">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 mb-2">{project.title}</h1>
                  <p className="text-gray-600 mb-2">{project.description}</p>
                  <p className="text-sm text-gray-500">Project ID: {project.projectId}</p>
                </div>
                <div className={`flex items-center px-3 py-1 rounded-full ${getPriorityColor(project.priority)}`}>
                  <Flag className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium capitalize">{project.priority}</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Project Progress</span>
                  <span className="text-sm text-gray-500">{calculateProgress()}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${calculateProgress()}%` }}
                  ></div>
                </div>
              </div>

              {/* Project Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  {format(new Date(project.startDate), 'MMM dd')} - {format(new Date(project.endDate), 'MMM dd, yyyy')}
                </div>
                <div className="flex items-center text-gray-600">
                  <DollarSign className="w-4 h-4 mr-2" />
                  LKR {project.totalAmount?.toLocaleString()}
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock className="w-4 h-4 mr-2" />
                  {formatDistance(new Date(project.endDate), new Date(), { addSuffix: true })}
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
                <div className="text-2xl font-bold">{project.tasks?.length || 0}</div>
                <div className="text-blue-100 text-sm">Total Tasks</div>
              </div>
              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
                <div className="text-2xl font-bold">LKR {getTotalPaid().toLocaleString()}</div>
                <div className="text-green-100 text-sm">Amount Paid</div>
              </div>
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
                <div className="text-2xl font-bold">{calculateProgress()}%</div>
                <div className="text-purple-100 text-sm">Completed</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Requirements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Project Requirements
          </h3>
          <p className="text-gray-700 whitespace-pre-wrap">{project.requirements}</p>
        </motion.div>

        {/* Tasks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-lg font-semibold mb-6">Project Tasks</h3>
          <div className="space-y-4">
            {project.tasks?.map((task, index) => (
              <motion.div
                key={task._id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start space-x-3">
                  <div className={`flex items-center ${task.status === 'completed' ? 'text-green-600' : task.status === 'in-progress' ? 'text-blue-600' : 'text-gray-600'}`}>
                    {getTaskIcon(task.status)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-semibold text-gray-800">{task.title}</h4>
                        {task.isAdditional && (
                          <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                            Additional
                          </span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(task.status)}`}>
                          {task.status}
                        </span>
                      </div>
                      <button
                        onClick={() => toggleTaskExpansion(task._id)}
                        className="flex items-center space-x-1 text-primary-600 hover:text-primary-700 text-sm"
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span>Comments ({task.comments?.length || 0})</span>
                      </button>
                    </div>
                    
                    {task.description && (
                      <p className="text-gray-600 mb-2">{task.description}</p>
                    )}
                    {task.dueDate && (
                      <p className="text-sm text-gray-500 mb-3">
                        Due: {format(new Date(task.dueDate), 'MMM dd, yyyy')}
                      </p>
                    )}

                    {/* Comments Section */}
                    {expandedTask === task._id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 border-t border-gray-200 pt-4"
                      >
                        {/* Existing Comments */}
                        <div className="space-y-3 mb-4">
                          {task.comments?.map((comment, commentIndex) => (
                            <div key={commentIndex} className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-center space-x-2 mb-1">
                                <User className="w-4 h-4 text-gray-500" />
                                <span className="text-sm font-medium text-gray-700">
                                  {comment.authorName}
                                </span>
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  comment.authorRole === 'admin' 
                                    ? 'bg-purple-100 text-purple-700' 
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {comment.authorRole}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatDistance(new Date(comment.createdAt), new Date(), { addSuffix: true })}
                                </span>
                              </div>
                              <p className="text-gray-800 text-sm">{comment.text}</p>
                            </div>
                          ))}
                          
                          {(!task.comments || task.comments.length === 0) && (
                            <p className="text-gray-500 text-sm text-center py-4">
                              No comments yet. Be the first to comment!
                            </p>
                          )}
                        </div>

                        {/* Add Comment */}
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={commentText[task._id] || ''}
                            onChange={(e) => setCommentText({
                              ...commentText,
                              [task._id]: e.target.value
                            })}
                            placeholder="Add a comment..."
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                addComment(task._id);
                              }
                            }}
                          />
                          <button
                            onClick={() => addComment(task._id)}
                            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}

            {(!project.tasks || project.tasks.length === 0) && (
              <div className="text-center py-8 text-gray-500">
                No tasks assigned yet
              </div>
            )}
          </div>
        </motion.div>

        {/* Payments */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-lg font-semibold mb-6 flex items-center">
            <DollarSign className="w-5 h-5 mr-2" />
            Payment Information
          </h3>

          <div className="space-y-4">
            {project.payments?.map((payment, index) => (
              <div key={payment._id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-semibold text-gray-800 capitalize">
                        {payment.type} Payment
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(payment.status)}`}>
                        {payment.status}
                      </span>
                    </div>
                    <p className="text-lg font-bold text-gray-800">
                      LKR {payment.amount?.toLocaleString()}
                    </p>
                    {payment.paymentDate && (
                      <p className="text-sm text-gray-500">
                        Paid: {format(new Date(payment.paymentDate), 'MMM dd, yyyy')}
                      </p>
                    )}
                  </div>
                  
                  {payment.status === 'pending' && (
                    <div>
                      <label className="bg-primary-600 text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-primary-700">
                        <Upload className="w-4 h-4 mr-2 inline" />
                        Upload Payment Slip
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              uploadPaymentSlip(payment._id, e.target.files[0]);
                            }
                          }}
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Document Management */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <DocumentManager 
            project={project} 
            onDocumentUpdate={fetchProject}
            userRole="student"
          />
        </motion.div>
      </div>
    </StudentLayout>
  );
};

export default StudentProjectDetail;